// Per-project metering surface (agency role only) — Build 15

import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { DEMO_PROJECT_ID } from '@/data/demo/preference-center/data'

interface MeteringData {
  period_start: string
  period_end: string
  twins_built: number
  bulk_queries_run: number
  pre_reads_run: number
  tokens_used: number
}

interface PlanLimits {
  twin_cap: number
  bulk_query_cap: number
  plan: string
}

const DEMO_METERING: MeteringData = {
  period_start: '2026-06-01',
  period_end: '2026-06-30',
  twins_built: 1200,
  bulk_queries_run: 7,
  pre_reads_run: 14,
  tokens_used: 2_420_000,
}

const DEMO_LIMITS: PlanLimits = {
  twin_cap: 1000,
  bulk_query_cap: 100,
  plan: 'agency_pro',
}

export function ProjectMeteringPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const isDemo = !projectId || projectId === DEMO_PROJECT_ID || projectId === 'demo'

  const [metering, setMetering] = useState<MeteringData | null>(null)
  const [limits, setLimits] = useState<PlanLimits | null>(null)
  const [loading, setLoading] = useState(true)

  const token = localStorage.getItem('echo_session') ?? ''
  const headers = { Authorization: `Bearer ${token}` }

  useEffect(() => {
    if (isDemo) { setMetering(DEMO_METERING); setLimits(DEMO_LIMITS); setLoading(false); return }
    Promise.all([
      fetch(`/api/v1/projects/${projectId}/metering`, { headers }).then(r => r.json() as Promise<MeteringData>),
      fetch(`/api/v1/projects/${projectId}/metering/limits`, { headers }).then(r => r.json() as Promise<PlanLimits>),
    ]).then(([m, l]) => { setMetering(m); setLimits(l) }).catch(() => {}).finally(() => setLoading(false))
  }, [projectId, isDemo])

  if (loading) return <div className="p-8 text-sm text-slate-400">Loading...</div>
  if (!metering || !limits) return <div className="p-8 text-sm text-slate-500">No metering data available.</div>

  const twinPct = limits.twin_cap > 0 ? (metering.twins_built / limits.twin_cap) * 100 : 0
  const queryPct = limits.bulk_query_cap > 0 ? (metering.bulk_queries_run / limits.bulk_query_cap) * 100 : 0

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-8">
      <div>
        <h1 className="text-xl font-medium text-deep-ink">Project metering</h1>
        <p className="text-sm text-slate-500 mt-0.5">
          {metering.period_start} – {metering.period_end}
          <span className="ml-2"><Badge variant="default" size="sm">{limits.plan.replace('_', ' ')}</Badge></span>
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card className="space-y-3">
          <p className="text-sm font-medium text-deep-ink">Twins built</p>
          <p className="text-3xl font-medium text-deep-ink">{metering.twins_built.toLocaleString()}</p>
          <div>
            <div className="flex justify-between text-xs text-slate-400 mb-1">
              <span>Plan cap: {limits.twin_cap.toLocaleString()}</span>
              <span>{twinPct.toFixed(0)}% used</span>
            </div>
            <div className="w-full h-1.5 bg-soft-panel rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${twinPct > 90 ? 'bg-red-400' : twinPct > 70 ? 'bg-uncertainty-amber' : 'bg-echo-indigo'}`}
                style={{ width: `${Math.min(100, twinPct)}%` }}
              />
            </div>
          </div>
        </Card>

        <Card className="space-y-3">
          <p className="text-sm font-medium text-deep-ink">Bulk queries run</p>
          <p className="text-3xl font-medium text-deep-ink">{metering.bulk_queries_run}</p>
          <div>
            <div className="flex justify-between text-xs text-slate-400 mb-1">
              <span>Plan cap: {limits.bulk_query_cap} / month</span>
              <span>{queryPct.toFixed(0)}% used</span>
            </div>
            <div className="w-full h-1.5 bg-soft-panel rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${queryPct > 90 ? 'bg-red-400' : queryPct > 70 ? 'bg-uncertainty-amber' : 'bg-echo-indigo'}`}
                style={{ width: `${Math.min(100, queryPct)}%` }}
              />
            </div>
          </div>
        </Card>

        <Card className="space-y-2">
          <p className="text-sm font-medium text-deep-ink">Evidence-backed pre-reads run</p>
          <p className="text-2xl font-medium text-deep-ink">{metering.pre_reads_run}</p>
          <p className="text-xs text-slate-400">Segment-level pre-reads this period</p>
        </Card>

        <Card className="space-y-2">
          <p className="text-sm font-medium text-deep-ink">Total tokens used</p>
          <p className="text-2xl font-medium text-deep-ink">{(metering.tokens_used / 1000).toFixed(0)}k</p>
          <p className="text-xs text-slate-400">Across all pipeline stages and queries this period</p>
        </Card>
      </div>

      <Card className="bg-soft-panel/40 space-y-3">
        <p className="text-sm font-medium text-deep-ink">Engagement billing reference</p>
        <p className="text-sm text-slate-600 leading-relaxed">
          This metering data is for your firm's reference when billing ad-hoc work beyond the initial engagement scope.
          Share the relevant metrics (twins built, queries run) with your client as part of the engagement deliverable.
          Echosome does not invoice your client directly — your firm owns the billing relationship.
        </p>
      </Card>
    </div>
  )
}
